/**
 * DbPopup - 弹出层组件
 *
 * 特性：
 * - 支持多个弹出位置（center/top/bottom/left/right）
 * - 支持遮罩层配置
 * - 支持关闭按钮
 * - 支持圆角配置
 * - 支持动画效果
 * - 支持安全区域
 * - 支持自定义头部/底部
 * - 按照开源组件规范编写
 */

import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import {
    View,
    Text,
    Modal,
    TouchableOpacity,
    TouchableWithoutFeedback,
    Animated,
    ScrollView,
    StatusBar,
    Platform,
    KeyboardAvoidingView,
} from 'react-native';
import { DbPopupProps, CloseIconPosition } from './DbPopup.types';
import {
    createStyles,
    POPUP_DEFAULTS,
    getPositionStyle,
    getContainerAlignment,
    SCREEN_HEIGHT,
} from './DbPopup.styles';
import DbIcon from '../DbIcon';

// ==================== DbPopup 组件 ====================

export const DbPopup: React.FC<DbPopupProps> = ({
    visible,
    onClose,
    position = 'center',
    overlay = true,
    overlayColor = POPUP_DEFAULTS.overlayColor,
    overlayOpacity = POPUP_DEFAULTS.overlayOpacity,
    closeOnClickOverlay = true,
    closable = false,
    closeIconPosition = 'top-right',
    closeIconColor = POPUP_DEFAULTS.closeIconColor,
    closeIconSize = POPUP_DEFAULTS.closeIconSize,
    round = true,
    borderRadius = POPUP_DEFAULTS.borderRadius,
    width,
    height,
    maxWidth,
    maxHeight,
    backgroundColor = POPUP_DEFAULTS.backgroundColor,
    safeAreaInsetTop = false,
    safeAreaInsetBottom = true,
    duration = POPUP_DEFAULTS.duration,
    lockScroll = true,
    zIndex,
    title,
    titleStyle,
    children,
    style,
    contentStyle,
    onOpened,
    onClosed,
    overlayStyle,
    header,
    footer,
    scrollable = false,
}) => {
    const styles = useMemo(() => createStyles(), []);
    
    // 内部状态：控制 Modal 的实际显示（等待关闭动画完成后才隐藏）
    const [internalVisible, setInternalVisible] = useState(visible);
    const [isAnimating, setIsAnimating] = useState(false);
    
    // 动画值
    const overlayAnim = useRef(new Animated.Value(0)).current;
    const popupAnim = useRef(new Animated.Value(0)).current;

    // 处理打开/关闭动画
    useEffect(() => {
        if (visible) {
            // 打开：先显示 Modal，再播放动画
            setInternalVisible(true);
            setIsAnimating(true);
            
            // 重置动画值
            overlayAnim.setValue(0);
            popupAnim.setValue(0);
            
            Animated.parallel([
                Animated.timing(overlayAnim, {
                    toValue: 1,
                    duration,
                    useNativeDriver: true,
                }),
                Animated.timing(popupAnim, {
                    toValue: 1,
                    duration,
                    useNativeDriver: true,
                }),
            ]).start(() => {
                setIsAnimating(false);
                onOpened?.();
            });
        } else if (internalVisible) {
            // 关闭：先播放动画，再隐藏 Modal
            setIsAnimating(true);
            
            Animated.parallel([
                Animated.timing(overlayAnim, {
                    toValue: 0,
                    duration,
                    useNativeDriver: true,
                }),
                Animated.timing(popupAnim, {
                    toValue: 0,
                    duration,
                    useNativeDriver: true,
                }),
            ]).start(() => {
                setIsAnimating(false);
                setInternalVisible(false);
                onClosed?.();
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    // 处理关闭
    const handleClose = useCallback(() => {
        onClose?.();
    }, [onClose]);

    // 处理遮罩层点击
    const handleOverlayPress = useCallback(() => {
        if (closeOnClickOverlay) {
            handleClose();
        }
    }, [closeOnClickOverlay, handleClose]);

    // 获取关闭按钮位置样式
    const getCloseButtonPositionStyle = (pos: CloseIconPosition) => {
        switch (pos) {
            case 'top-left':
                return styles.closeButtonTopLeft;
            case 'top-right':
                return styles.closeButtonTopRight;
            case 'bottom-left':
                return styles.closeButtonBottomLeft;
            case 'bottom-right':
                return styles.closeButtonBottomRight;
            default:
                return styles.closeButtonTopRight;
        }
    };

    // 计算弹出层动画样式
    const getPopupAnimatedStyle = () => {
        switch (position) {
            case 'center':
                return {
                    opacity: popupAnim,
                    transform: [
                        {
                            scale: popupAnim.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0.9, 1],
                                extrapolate: 'clamp',
                            }),
                        },
                    ],
                };
            case 'top':
                return {
                    transform: [
                        {
                            translateY: popupAnim.interpolate({
                                inputRange: [0, 1],
                                outputRange: [-300, 0],
                                extrapolate: 'clamp',
                            }),
                        },
                    ],
                };
            case 'bottom':
                return {
                    transform: [
                        {
                            translateY: popupAnim.interpolate({
                                inputRange: [0, 1],
                                outputRange: [SCREEN_HEIGHT, 0],
                                extrapolate: 'clamp',
                            }),
                        },
                    ],
                };
            case 'left':
                return {
                    transform: [
                        {
                            translateX: popupAnim.interpolate({
                                inputRange: [0, 1],
                                outputRange: [-300, 0],
                                extrapolate: 'clamp',
                            }),
                        },
                    ],
                };
            case 'right':
                return {
                    transform: [
                        {
                            translateX: popupAnim.interpolate({
                                inputRange: [0, 1],
                                outputRange: [300, 0],
                                extrapolate: 'clamp',
                            }),
                        },
                    ],
                };
            default:
                return { opacity: popupAnim };
        }
    };

    // 容器对齐方式
    const containerAlignment = getContainerAlignment(position);

    // 弹出层位置样式
    const positionStyle = getPositionStyle(
        position,
        round,
        borderRadius,
        width,
        height,
        maxWidth,
        maxHeight
    );

    // 安全区域处理
    const safeAreaStyle = {
        paddingTop: safeAreaInsetTop && Platform.OS === 'ios' 
            ? (StatusBar.currentHeight || 44) 
            : 0,
        paddingBottom: safeAreaInsetBottom && position === 'bottom' ? 34 : 0,
    };

    // 渲染头部
    const renderHeader = () => {
        if (header) return header;
        if (!title) return null;

        return (
            <View style={styles.header}>
                <Text style={[styles.headerTitle, titleStyle]}>{title}</Text>
            </View>
        );
    };

    // 渲染关闭按钮
    const renderCloseButton = () => {
        if (!closable) return null;

        return (
            <TouchableOpacity
                style={[
                    styles.closeButton,
                    getCloseButtonPositionStyle(closeIconPosition),
                ]}
                onPress={handleClose}
                hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
            >
                <DbIcon
                    name="close"
                    family="Ionicons"
                    size={closeIconSize}
                    color={closeIconColor}
                />
            </TouchableOpacity>
        );
    };

    // 渲染内容
    const renderContent = () => {
        if (scrollable) {
            return (
                <ScrollView
                    style={styles.contentScrollable}
                    contentContainerStyle={contentStyle}
                    showsVerticalScrollIndicator={false}
                    bounces={false}
                >
                    {children}
                </ScrollView>
            );
        }

        return (
            <View style={[styles.content, contentStyle]}>
                {children}
            </View>
        );
    };

    // 渲染底部
    const renderFooter = () => {
        if (!footer) return null;

        return (
            <View style={styles.footer}>
                {footer}
            </View>
        );
    };

    // 如果内部状态为隐藏，则不渲染
    if (!internalVisible) {
        return null;
    }

    return (
        <Modal
            visible={internalVisible}
            transparent
            animationType="none"
            statusBarTranslucent
            onRequestClose={handleClose}
        >
            <KeyboardAvoidingView
                style={[
                    styles.modalContainer,
                    containerAlignment,
                    zIndex !== undefined && { zIndex },
                ]}
                behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            >
                {/* 遮罩层 */}
                {overlay && (
                    <TouchableWithoutFeedback onPress={handleOverlayPress}>
                        <Animated.View
                            style={[
                                styles.overlay,
                                {
                                    backgroundColor: overlayColor,
                                    opacity: overlayAnim.interpolate({
                                        inputRange: [0, 1],
                                        outputRange: [0, overlayOpacity],
                                    }),
                                },
                                overlayStyle,
                            ]}
                        />
                    </TouchableWithoutFeedback>
                )}

                {/* 弹出层 */}
                <Animated.View
                    style={[
                        styles.popupContainer,
                        positionStyle,
                        { backgroundColor },
                        safeAreaStyle,
                        getPopupAnimatedStyle(),
                        style,
                    ]}
                >
                    {renderCloseButton()}
                    {renderHeader()}
                    {renderContent()}
                    {renderFooter()}
                </Animated.View>
            </KeyboardAvoidingView>
        </Modal>
    );
};

DbPopup.displayName = 'DbPopup';

export default DbPopup;
export type {
    DbPopupProps,
    PopupPosition,
    CloseIconPosition,
    PopupActionConfig,
} from './DbPopup.types';
